import { useEffect, useRef, useState } from "react"
import { FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa"

import { join } from "@/utils/join"
import { usePopUpMenu } from "@/utils/usePopUpMenu"

interface Props<T extends string> {
  selectedOption?: T
  options: readonly T[]
  onSelectionChange: (selection: T) => void
  className?: string
}

export const DropdownMenu = <T extends string>({
  selectedOption,
  options,
  onSelectionChange,
  className,
}: Props<T>) => {
  const ref = useRef<HTMLDivElement>(null)
  const [displayMenu, setDisplayMenu] = usePopUpMenu(ref)
  const [selected, setSelected] = useState(selectedOption ?? options[0])
  const toggleOpenCloseState = () => setDisplayMenu(!displayMenu)

  useEffect(() => {
    if (selectedOption) {
      setSelected(selectedOption)
    }
  }, [selectedOption])

  const handleSelection = (option: T) => {
    setSelected(option)
    onSelectionChange(option)
  }

  return (
    <div
      className={join(
        className,
        "relative flex cursor-pointer items-center justify-between px-md display-xs-regular leading-[0] text-secondary transition-all duration-200 ease-in-out",
      )}
      onClick={toggleOpenCloseState}
    >
      <div>{selected}</div>
      <div>
        {displayMenu ? (
          <FaChevronUp className="relative ml-xl text-[12px] text-quaternary_on-brand" />
        ) : (
          <FaChevronDown className="relative ml-xl text-[12px] text-quaternary_on-brand" />
        )}
      </div>
      {displayMenu && (
        <div
          className="absolute top-[30px] left-0 z-30 w-[140px] rounded-sm bg-tertiary shadow-lg shadow-(color:--bg-primary)"
          ref={ref}
        >
          {options.map((option) => (
            <div
              className={join(
                selected === option ? "text-brand-primary" : "inherit",
                "flex h-(--density-md) items-center justify-between rounded-sm px-2xl py-3xl hover:bg-quaternary",
              )}
              key={option}
              onClick={() => handleSelection(option)}
            >
              {option}
              {selected === option && (
                <FaCheck className="relative ml-xl text-[12px] text-quaternary_on-brand" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
